import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { makeStyles } from "tss-react/mui"
import MaterialReactTable, { MaterialReactTableProps, MRT_TableInstance } from "material-react-table"
import type { Updater } from "@tanstack/react-table"
import { getMaterialReactTableTranslation } from "../localizationLib"
import { usePagination } from "../../../shared/hooks/usePagination"
import { selectSelectedLocale } from "../localizationSelectors"
import { StrictOmit } from "../../../lib/lang"
import { sanitizeSearchQuery } from "../../../lib/stringLib"

const useStyles = makeStyles()((_theme) => ({
    font: {
        fontSize: "14px",
    },
    searchField: {
        height: "100%",
        justifyContent: "center",
    },
}))

export type LocMrtProps<TData extends Record<string, any>> = StrictOmit<MaterialReactTableProps<TData>, "localization">
export type LocalizedMRTProps<TData extends Record<string, any>> = {
    managePaginationViaUrl?: boolean
    defaultPageIndex?: number
    defaultPageSize?: number
    scrollToTopOnPageChange?: boolean
} & MaterialReactTableProps<TData>

export function LocalizedMaterialReactTable<TData extends Record<string, any> = {}>({
    managePaginationViaUrl = true,
    defaultPageIndex = 0,
    defaultPageSize = 100,
    scrollToTopOnPageChange = false,
    ...props
}: LocalizedMRTProps<TData>) {
    const selectedLocale = useSelector(selectSelectedLocale)
    const translation = useMemo(() => getMaterialReactTableTranslation(selectedLocale), [selectedLocale])

    const { classes, cx } = useStyles()
    const [pagination, setPagination] = usePagination(managePaginationViaUrl, defaultPageIndex, defaultPageSize)

    const searchInputRef = useRef<HTMLInputElement>(null)
    const internalTableInstanceRef = useRef<MRT_TableInstance<TData> | null>(null)
    const tableInstanceRef = props.tableInstanceRef ?? internalTableInstanceRef

    const [internalGlobalFilter, setInternalGlobalFilter] = useState<string | undefined>(undefined)
    const globalFilter = props.state?.globalFilter ?? internalGlobalFilter

    const handleGlobalFilterChange = (updater: Updater<string | undefined>) => {
        const nextValue = updater instanceof Function ? updater(globalFilter) : updater
        const sanitizedValue = nextValue === undefined ? undefined : sanitizeSearchQuery(nextValue)

        if (props.onGlobalFilterChange) {
            props.onGlobalFilterChange(sanitizedValue)
        } else {
            setInternalGlobalFilter(sanitizedValue)
        }
    }

    useEffect(() => {
        if (!scrollToTopOnPageChange) return
        tableInstanceRef.current?.refs.tableContainerRef.current?.scrollTo({ top: 0 })
    }, [pagination.pageIndex, scrollToTopOnPageChange, tableInstanceRef])

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "/") {
            event.preventDefault()
            const searchButton = document.getElementById("mrt-search-button")

            if (searchButton) {
                ;(searchButton as HTMLElement).click()
                setTimeout(() => {
                    const searchInput = document.querySelector(
                        'input[data-testid="MRT_SearchTextField"]',
                    ) as HTMLInputElement
                    if (searchInput) {
                        searchInput.focus()
                    }
                }, 100)
            }
        }
    }

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown)
        return () => {
            window.removeEventListener("keydown", handleKeyDown)
        }
    }, [])

    return (
        <MaterialReactTable
            {...props}
            tableInstanceRef={tableInstanceRef}
            muiTableBodyProps={{
                sx: {
                    fontSize: "14px",
                },
                className: classes.font,
            }}
            muiTableProps={{
                sx: {
                    tableLayout: "fixed",
                },
            }}
            localization={translation}
            initialState={{ ...props.initialState, pagination }}
            state={{ ...props.state, pagination, globalFilter }}
            onGlobalFilterChange={handleGlobalFilterChange}
            onPaginationChange={setPagination}
            muiTablePaginationProps={{
                rowsPerPageOptions: [10, 50, 100, 1000],
            }}
            muiSearchTextFieldProps={{
                ...props.muiSearchTextFieldProps,
                className: cx(
                    classes.searchField,
                    typeof props.muiSearchTextFieldProps === "function"
                        ? undefined
                        : props.muiSearchTextFieldProps?.className,
                ),
                inputRef: searchInputRef,
            }}
        />
    )
}
